import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Space,
  Row,
  Col,
  Statistic,
  Tag,
  Select,
  Descriptions,
  Timeline,
  Alert,
  Empty,
  Spin,
} from 'antd';
import {
  RocketOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  BranchesOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MainCard from '@/components/MainCard';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import { useMbmrAlias } from '@/hooks/useAppSetting';
import { get_final_recipe_list, get_function } from '@/utils/api';
import { formatDate, formatDateTime } from '@/utils/datetime';
import './RecipeVersioning.css';

const { Title, Text } = Typography;

const APPROVAL_COLOR = {
  approved: 'success',
  pending: 'processing',
  rejected: 'error',
  no_approval_flow: 'default',
};

/**
 * RecipeVersioning Page — read-only version lineage for a master recipe.
 *
 * Data: `GET /bp/get_all_recipe_details` (recipe picker) and
 * `GET /bp/get_recipe_version_history?recipe_id=` (root recipe + derived versions, change-control id,
 * created-by, approval status). Approvals themselves are performed in Recipe Approval; nothing here writes.
 */
const RecipeVersioning = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const mbmrAlias = useMbmrAlias();
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';

  useEffect(() => {
    get_final_recipe_list((res) => {
      const list = res?.data?.data || [];
      setRecipes(list.map((r) => ({ id: r.id ?? r.recipe_id, name: r.name || r.recipe_name || `Recipe ${r.id}` })));
    });
  }, []);

  useEffect(() => {
    if (!selectedRecipe) return;
    setLoading(true);
    setError(null);
    setHistory(null);
    get_function(
      '/bp/get_recipe_version_history',
      (res) => {
        setLoading(false);
        if (res?.data?.status === 'success') {
          setHistory(res.data.data || null);
        } else {
          setError(res?.data?.message || 'Version history could not be loaded');
        }
      },
      { recipe_id: selectedRecipe }
    );
  }, [selectedRecipe]);

  const root = history?.root_recipe;
  const versions = history?.versions || [];
  const versionLabel = (r) => (r ? `v${r.major_version ?? 1}.${r.minor_version ?? 0}` : '—');
  const latest = versions.length ? versions[versions.length - 1] : null;
  const approvedCount = versions.filter((v) => v.approval_status === 'approved').length;

  return (
    <MainCard
      className="m-2.5 border border-gray-300 shadow-md"
      title={
        <Space align="center">
          <div className="flex items-center gap-3">
            <RocketOutlined className="ml-4 text-blue-600" style={{ fontSize: 24 }} />
            <Title level={3} style={{ margin: 0 }}>
              {t('Recipe Versioning & Change Control')}
            </Title>
          </div>
        </Space>
      }
      content={false}
    >
      <div style={{ padding: 24 }}>
        {/* Header Stats — live counts for the selected recipe */}
        <Card style={{ marginBottom: 24, backgroundColor: isDark ? '#0F172A' : defaultColors.primary }}>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Derived versions</Text>}
                value={history ? history.total_versions ?? versions.length : '—'}
                prefix={<BranchesOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Approved versions</Text>}
                value={history ? approvedCount : '—'}
                prefix={<SafetyOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Current version</Text>}
                value={root ? versionLabel(latest || root) : '—'}
                prefix={<HistoryOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Last change</Text>}
                value={latest?.created_at ? formatDate(latest.created_at) : root?.created_at ? formatDate(root.created_at) : '—'}
                prefix={<ClockCircleOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff', fontSize: 16 }}
              />
            </Col>
          </Row>
        </Card>

        {/* Recipe Selection */}
        <Card
          title={<span style={{ color: textColor }}>Select {mbmrAlias} to view its version lineage</span>}
          style={{ marginBottom: 24, backgroundColor: cardBgColor, borderColor }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Select
              style={{ width: '100%' }}
              placeholder={`Select a ${mbmrAlias}`}
              onChange={setSelectedRecipe}
              value={selectedRecipe}
              size="large"
              showSearch
              optionFilterProp="label"
              options={recipes.map((r) => ({ value: r.id, label: r.name }))}
            />

            {root && (
              <Descriptions
                bordered
                size="small"
                column={{ xs: 1, sm: 2, md: 3 }}
                labelStyle={{ color: secondaryTextColor, backgroundColor: isDark ? defaultColors.palette.gray[700] : undefined }}
                contentStyle={{ color: textColor, backgroundColor: isDark ? defaultColors.palette.gray[800] : undefined }}
              >
                <Descriptions.Item label={`${mbmrAlias} ID`}>{root.id}</Descriptions.Item>
                <Descriptions.Item label="Code">{root.recipe_code || '—'}</Descriptions.Item>
                <Descriptions.Item label="Root version">
                  <Tag color="blue">{versionLabel(root)}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Valid from">{root.valid_from ? formatDate(root.valid_from) : '—'}</Descriptions.Item>
                <Descriptions.Item label="Valid to">{root.valid_to ? formatDate(root.valid_to) : 'open'}</Descriptions.Item>
                <Descriptions.Item label="Created">{root.created_at ? formatDateTime(root.created_at) : '—'}</Descriptions.Item>
                {root.change_reason && (
                  <Descriptions.Item label="Change reason" span={3}>{root.change_reason}</Descriptions.Item>
                )}
              </Descriptions>
            )}
          </Space>
        </Card>

        {/* Version lineage */}
        {!selectedRecipe ? (
          <Card style={{ backgroundColor: cardBgColor, borderColor }}>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <FileTextOutlined style={{ fontSize: 64, color: secondaryTextColor, marginBottom: 16 }} />
              <Title level={4} style={{ color: secondaryTextColor }}>No {mbmrAlias} selected</Title>
              <Text style={{ color: secondaryTextColor }}>
                Pick a {mbmrAlias} above to see every version derived from it, who created it and under which change control.
              </Text>
            </div>
          </Card>
        ) : (
          <Card
            title={<span style={{ color: textColor }}>Version lineage</span>}
            extra={<Text type="secondary">Approvals are performed in Recipe Approval — this view is read-only.</Text>}
            style={{ marginBottom: 24, backgroundColor: cardBgColor, borderColor }}
          >
            {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
            {!loading && error && <Alert type="error" showIcon message={error} />}
            {!loading && !error && history && versions.length === 0 && (
              <Empty description={`No derived versions yet — ${versionLabel(root)} is the only version of this ${mbmrAlias}.`} />
            )}
            {!loading && !error && versions.length > 0 && (
              <Timeline
                mode="left"
                items={[
                  {
                    color: 'gray',
                    label: root?.created_at ? formatDateTime(root.created_at) : '',
                    children: (
                      <div>
                        <Text strong style={{ color: textColor }}>{versionLabel(root)} · {root?.name}</Text>
                        <div><Text type="secondary">Root {mbmrAlias} · {root?.recipe_code || '—'}</Text></div>
                      </div>
                    ),
                  },
                  ...versions.map((v) => ({
                    color: v.approval_status === 'approved' ? 'green' : v.approval_status === 'rejected' ? 'red' : 'blue',
                    label: v.created_at ? formatDateTime(v.created_at) : '',
                    children: (
                      <div>
                        <Space wrap>
                          <Text strong style={{ color: textColor }}>Version {v.version_number} · {versionLabel(v)}</Text>
                          <Tag color={APPROVAL_COLOR[v.approval_status] || 'default'}>
                            {(v.approval_status || 'unknown').replace(/_/g, ' ')}
                          </Tag>
                          {v.change_control_id && <Tag color="purple">{v.change_control_id}</Tag>}
                        </Space>
                        <div><Text style={{ color: textColor }}>{v.recipe_name}</Text> <Text type="secondary">({v.recipe_code})</Text></div>
                        <div>
                          <Text type="secondary">
                            Created by {v.created_by || '—'}
                            {v.parent_recipe_name ? ` · from ${v.parent_recipe_name}` : ''}
                            {v.validation_duration_days != null ? ` · validated in ${v.validation_duration_days} d` : ''}
                          </Text>
                        </div>
                        {(v.change_reason || v.notes) && (
                          <div style={{ marginTop: 4 }}><Text style={{ color: textColor }}>{v.change_reason || v.notes}</Text></div>
                        )}
                        {Array.isArray(v.approval_details) && v.approval_details.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            {v.approval_details.map((a, i) => (
                              <Tag key={i} color={a.status === 'approved' ? 'success' : 'default'}>
                                {a.role || a.approver || 'approver'}{a.status ? ` · ${a.status}` : ''}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    ),
                  })),
                ]}
              />
            )}
          </Card>
        )}
      </div>
    </MainCard>
  );
};

export default RecipeVersioning;
